export const SEND_WINDOW_TIME_ZONE_HINT = "24-hour, in the recipient's time zone";
export const MARK_ROUTES = {
    ROOT: '/mark',
    TEMPLATES: '/mark/templates',
    TEMPLATE_NEW: '/mark/templates/new',
    TEMPLATE_DETAIL: '/mark/templates/:templateId',
    CONTACTS: '/mark/contacts',
    CONTACT_DETAIL: '/mark/contacts/:profileId',
    SEGMENTS: '/mark/segments',
    SEGMENT_NEW: '/mark/segments/new',
    SEGMENT_IMPORT: '/mark/segments/import',
    SEGMENT_DETAIL: '/mark/segments/:segmentId',
    SEGMENT_EDIT: '/mark/segments/:segmentId/edit',
    DRIPS: '/mark/drips',
    DRIP_DETAIL: '/mark/drips/:dripId',
    DRIP_VERSION: '/mark/drips/:dripId/versions/:versionNumber',
    DEV_TEMPLATE_EDITOR: '/mark/dev/template-editor'
};
export const SEGMENT_ERROR_LABELS = {
    segment_id_required: 'Segment ID is required',
    target_kind_required: 'Target kind is required',
    filters_required: 'At least one filter is required',
    name_required: 'Name is required',
    slug_required: 'Slug is required',
    rows_required: 'At least one row is required',
    invalid_column: 'Invalid column selection',
    invalid_filters: 'One or more filters are invalid',
    invalid_match: 'Invalid match type',
    invalid_target_kind: 'Invalid target kind',
    invalid_attribute: 'Unknown or unavailable attribute',
    invalid_operator: 'Invalid filter operator',
    invalid_filter_values: 'One or more filter values are invalid',
    invalid_field_value: 'Invalid field value',
    segment_in_use: 'Segment is in use by active drips or blasts',
    imported_segment_not_editable: 'Imported segments cannot be edited — use CSV replace',
    segment_attribute_unavailable: 'Attribute is no longer available',
    segment_source_cycle: 'Source segment would create a cycle',
    source_segment_not_found: 'Source segment not found',
    source_segment_target_kind_mismatch: 'Source segment target kind does not match',
    segment_import_failed: 'Import failed'
};
export const HTML_TEMPLATE_ERROR_LABELS = {
    name_required: 'Add a template name.',
    invalid_name: 'Template name must be text of 255 characters or fewer.',
    library_requires_email: 'Library templates must be email templates.',
    html_animation_not_allowed: 'SVG animation is not supported in email templates.',
    html_noscript_not_allowed: 'Remove <noscript> from the template; email clients do not run scripts.',
    html_cdata_not_allowed: 'Remove CDATA sections (<![CDATA[ ]]>) from the template.',
    html_comments_not_allowed: 'Remove HTML comments (<!-- -->) from the template.',
    html_structure_in_condition: 'Do not put <html>, <head> or <body> inside an if block.',
    html_body_required: 'Add an explicit <body> tag to the HTML document.',
    html_too_large: 'Template HTML must be 256 KB or smaller.',
    html_too_many_tokens: 'Use no more than 500 template tokens.',
    html_token_in_tag: 'Move template tokens into supported text or quoted attributes.',
    html_preheader_conflict: 'Your HTML already has a hidden preheader. Clear Preview text or remove it from the HTML.',
    html_invalid_structure: 'Fix malformed HTML: check tag names, nesting, closing tags, and duplicate attributes.',
    html_target_kind_mismatch: 'The selected audience no longer matches this campaign. Refresh and try again.',
    html_unsubscribe_required: 'Add a recipient unsubscribe link in the email body: <a href="{{UNSUBSCRIBE_URL}}">Unsubscribe</a>. Keep it outside any {{#if}} blocks.',
    template_unsafe_html: 'Remove unsafe HTML or links from the template.',
    html_render_unsafe: 'The template cannot render safely. Check its HTML structure and variables.',
    html_required: 'HTML content is required.',
    missing_subject: 'A subject is required.',
    invalid_html: 'HTML content must be text.',
    invalid_subject: 'The subject must be text with no more than 255 characters.',
    invalid_preheader: 'The preheader must be text with no more than 255 characters.'
};
export const htmlTemplateErrorLabel = (code) => {
    const unknownVariable = 'html_unknown_variable:';
    if (code.startsWith(unknownVariable))
        return `Unknown template variable: ${code.slice(unknownVariable.length)}.`;
    const label = HTML_TEMPLATE_ERROR_LABELS[code];
    return typeof label === 'string' ? label : code;
};
export const BLAST_ERROR_LABELS = {
    ...HTML_TEMPLATE_ERROR_LABELS,
    missing_blast_id: 'Blast ID is missing',
    blast_id_required: 'Blast ID is required',
    blast_id_or_drip_id_required: 'Blast ID or Drip ID is required',
    blast_not_draft: 'Only DRAFT blasts can be edited',
    segment_empty: 'Segment has no matching recipients',
    segment_too_large: 'Segment exceeds the 50,000 sync-publish cap — use Schedule',
    no_variants: 'At least one variant is required',
    variant_ratios_invalid: 'Variant ratios must sum to 100',
    template_missing: 'Every variant needs a template before this blast can be sent',
    missing_subject: 'Subject is required for each variant',
    template_unknown_variable: 'Template contains an unknown variable',
    variable_requires_target_kind: 'Variable requires a specific target kind',
    template_compile_failed: 'Template failed to compile',
    template_unsafe_html: 'Template contains unsafe HTML',
    url_missing_env: 'A Mark URL is not configured for this environment',
    audience_segment_mismatch: 'Segment target kind does not match this blast',
    immutable_field_name: 'Name cannot be changed after create',
    immutable_field_slug: 'Slug cannot be changed after create',
    immutable_field_message_class: 'Message class cannot be changed after create',
    immutable_field_delivery_scope: 'Delivery scope cannot be changed after create',
    variants_not_allowed_at_create: 'Variants must be added after the blast shell is created',
    blast_not_found: 'Blast not found',
    blast_not_publishable: 'This blast can no longer be published — its status has changed',
    blast_not_schedulable: 'Only DRAFT or SCHEDULED blasts can be scheduled',
    blast_not_cancellable: 'This blast can no longer be cancelled — its status has changed',
    blast_not_resumable: 'This blast can no longer be resumed — its status has changed',
    mark_disabled: 'Mark is disabled in this environment',
    segment_required: 'Pick an audience segment before sending',
    segment_not_found: 'Segment not found',
    slug_already_exists: 'A blast with this name already exists — choose a different name',
    missing_name: 'Name is required',
    invalid_scheduled_at: 'Scheduled time is not a valid date and time',
    missing_scheduled_at: 'Scheduled time is required',
    invalid_local_send_time: 'Local send time must be HH:MM',
    invalid_schedule_mode: 'Invalid schedule mode',
    invalid_account_recipients: 'Invalid account recipients option',
    invalid_delivery_scope: 'Invalid delivery scope',
    invalid_message_class: 'Invalid message class',
    invalid_field_value: 'Invalid field value',
    template_fields_not_allowed: 'Template fields are edited per variant, not on the blast',
    too_many_variants: 'Too many variants',
    invalid_variant: 'A variant is invalid',
    missing_variant_label: 'Each variant needs a label',
    duplicate_variant_label: 'Variant labels must be unique',
    invalid_variant_ratio: 'Each variant ratio must be a non-negative whole number',
    variant_template_fields_not_allowed: 'Variant templates are edited on the Content tab'
};
export const DRIP_VALIDATION_MESSAGES = {
    step_missing_id: {
        message: 'A step has no ID.',
        hint: 'Edit or recreate the step.',
        destination: 'steps'
    },
    duplicate_step_id: {
        message: 'Two steps share an ID.',
        hint: 'Recreate the duplicate step.',
        destination: 'steps'
    },
    entry_step_invalid: {
        message: 'Choose a starting step.',
        hint: 'Set an existing step as entry.',
        destination: 'steps'
    },
    no_activity_step: {
        message: 'Add an activity step before activating.',
        hint: 'Review the drip configuration and try again.',
        destination: 'steps'
    },
    invalid_step_format: {
        message: 'A step has an invalid structure.',
        hint: 'Review its configuration.',
        destination: 'steps'
    },
    invalid_field_value: {
        message: 'A step field has an invalid value.',
        hint: 'Correct its configuration.',
        destination: 'steps'
    },
    step_missing_exit_reason: {
        message: 'A step needs an exit reason.',
        hint: 'Choose one in its configuration.',
        destination: 'steps'
    },
    check_segment_missing_segment_id: {
        message: 'A segment-check step needs a segment.',
        hint: 'Select one.',
        destination: 'steps'
    },
    invalid_segment_id: {
        message: "A step's segment selection is invalid.",
        hint: 'Select an existing segment.',
        destination: 'steps'
    },
    check_segment_missing_branch: {
        message: 'A segment check needs both branches.',
        hint: 'Set both destinations.',
        destination: 'steps'
    },
    ab_arms_invalid: {
        message: 'An A/B split needs valid A and B destinations.',
        hint: 'Configure both.',
        destination: 'steps'
    },
    ab_ratio_invalid: {
        message: 'An A/B ratio is invalid.',
        hint: 'Choose a valid split ratio.',
        destination: 'steps'
    },
    invalid_wait_for_duration: {
        message: 'A wait duration is invalid.',
        hint: 'Correct its value.',
        destination: 'steps'
    },
    zero_wait_for_duration: {
        message: 'A wait duration must be greater than zero.',
        hint: 'Increase it.',
        destination: 'steps'
    },
    unregistered_external_action: {
        message: 'An activity uses an unavailable external action.',
        hint: 'Select a registered action or ask an administrator.',
        destination: 'steps'
    },
    step_dangling_reference: {
        message: 'A branch points to a missing step.',
        hint: 'Choose an existing destination.',
        destination: 'steps'
    },
    step_orphan_node: {
        message: 'A step cannot be reached from the entry.',
        hint: 'Connect or remove it.',
        destination: 'steps'
    },
    step_illegal_cycle: {
        message: 'A loop needs a timed segment check.',
        hint: 'Add a wait or remove the loop.',
        destination: 'steps'
    },
    stale_segment_reference: {
        message: 'A step references an unavailable segment.',
        hint: 'Choose another segment.',
        destination: 'steps'
    },
    step_segment_target_kind_incompatible: {
        message: "A step's segment targets the wrong kind of recipient.",
        hint: 'Choose a compatible segment.',
        destination: 'steps'
    },
    send_window_out_of_range: {
        message: 'The send window is outside allowed hours.',
        hint: 'Review the send-window validation and correct its configuration.',
        destination: 'steps'
    },
    drip_trigger_prerequisite_missing: {
        message: 'The trigger setup is incomplete.',
        hint: 'Review trigger prerequisites.',
        destination: 'overview'
    },
    re_entry_days_required: {
        message: 'Set re-entry days for the selected re-entry policy.',
        hint: 'Review the drip configuration and try again.',
        destination: 'overview'
    },
    template_missing: {
        message: 'A send step needs a template.',
        hint: 'Add one.',
        destination: 'steps'
    },
    template_compile_failed: {
        message: 'A template could not be compiled.',
        hint: 'Review its blocks and validation feedback.',
        destination: 'steps'
    },
    template_unknown_variable: {
        message: 'A template uses an unknown variable.',
        hint: 'Choose a supported variable.',
        destination: 'steps'
    },
    variable_requires_target_kind: {
        message: 'A template variable is incompatible with the recipient kind.',
        hint: 'Replace it with a compatible variable.',
        destination: 'steps'
    },
    template_unsafe_html: {
        message: 'A template contains unsupported HTML.',
        hint: 'Remove the unsafe markup.',
        destination: 'steps'
    },
    template_render_failed: {
        message: 'A template could not be rendered.',
        hint: 'Review its content and variables.',
        destination: 'steps'
    },
    template_invalid_block_structure: {
        message: 'A template block has an invalid structure.',
        hint: 'Correct or recreate it.',
        destination: 'steps'
    },
    sms_template_invalid_block: {
        message: 'An SMS template contains an unsupported block.',
        hint: 'Use an SMS-compatible block.',
        destination: 'steps'
    },
    template_unknown_block_type: {
        message: 'A template contains an unknown block type.',
        hint: 'Replace that block.',
        destination: 'steps'
    },
    template_missing_required_field: {
        message: 'A template block is missing a required field.',
        hint: 'Complete it.',
        destination: 'steps'
    },
    template_empty_required_field: {
        message: 'A required template field is empty.',
        hint: 'Enter its value.',
        destination: 'steps'
    },
    template_invalid_conditional_operator: {
        message: 'A template condition uses an invalid operator.',
        hint: 'Choose a supported operator.',
        destination: 'steps'
    },
    template_invalid_link: {
        message: 'A template link is invalid.',
        hint: 'Correct its URL.',
        destination: 'steps'
    },
    url_missing_env: {
        message: 'A required URL setting is unavailable.',
        hint: 'Ask a platform administrator to configure it.',
        destination: 'overview'
    },
    mark_disabled: {
        message: 'Mark is currently disabled.',
        hint: 'Ask a platform administrator to enable it.'
    },
    segment_empty: {
        message: 'The enrollment segment has no matches.',
        hint: 'Choose another audience or wait for matches.',
        destination: 'overview'
    },
    drip_not_activatable: {
        message: 'This drip cannot be activated in its current state.',
        hint: 'Refresh and review its status.',
        destination: 'overview'
    },
    drip_not_found: {
        message: 'This drip is unavailable.',
        hint: 'Return to the Drips list and select an existing drip.',
        destination: 'list'
    },
    no_draft_version: {
        message: 'No draft version is available.',
        hint: 'Open Steps and create or select an editable draft.',
        destination: 'steps'
    }
};
export const getDripValidationMessage = (code) => {
    if (!code) {
        return {
            message: 'Could not complete the request. Try again.',
            hint: 'If the issue continues, contact support.'
        };
    }
    if (code.startsWith('html_') ||
        Object.prototype.hasOwnProperty.call(HTML_TEMPLATE_ERROR_LABELS, code)) {
        return {
            message: htmlTemplateErrorLabel(code),
            hint: 'Open the email step and correct its template. Your saved draft is kept.',
            destination: 'steps'
        };
    }
    return Object.prototype.hasOwnProperty.call(DRIP_VALIDATION_MESSAGES, code)
        ? DRIP_VALIDATION_MESSAGES[code]
        : {
            message: 'This drip could not pass validation.',
            hint: 'Try checking again. If the issue continues, contact support.'
        };
};
export const DRIP_ERROR_LABELS = {
    ...HTML_TEMPLATE_ERROR_LABELS,
    slug_already_exists: 'A drip with this name already exists. If an earlier Create attempt timed out, it may have been created — check the Drips list',
    missing_drip_id: 'Drip ID is missing',
    drip_id_required: 'Drip ID is required',
    drip_not_pausable: 'Only active drips can be paused',
    invalid_re_entry_policy: 'Invalid re-entry policy',
    instance_already_enrolled: 'This demo profile is already enrolled in this drip. Reset it before enrolling again.',
    active_version_immutable: 'The active version cannot be edited directly',
    blast_id_or_drip_id_required: 'Blast ID or Drip ID is required',
    immutable_field_name: 'Name cannot be changed after creation',
    immutable_field_slug: 'Slug cannot be changed after creation',
    immutable_field_trigger_type: 'Trigger type cannot be changed after creation',
    immutable_field_prospect_type: 'Prospect type cannot be changed after creation',
    immutable_field_feature_gate: 'Feature gate cannot be changed after creation',
    ...Object.fromEntries(Object.entries(DRIP_VALIDATION_MESSAGES).map(([code, { message }]) => [code, message]))
};
export const EXIT_REASON_LABELS = {
    SEGMENT_EXIT: 'Segment exit',
    COMPLETED: 'Completed',
    SUPPRESSED: 'Suppressed',
    SUPERSEDED: 'Superseded',
    STALE: 'Stale',
    CONVERTED: 'Converted',
    PROSPECT_MISSING: 'Prospect missing',
    MANUAL: 'Manual',
    ROLLED_BACK: 'Rolled back',
    SEND_FAILED: 'Send failed',
    MAX_ATTEMPTS_EXCEEDED: 'Max attempts exceeded'
};
export const SKIPPED_REASON_LABELS = {
    suppressed: 'Suppressed',
    invalid_email: 'Invalid email',
    suppressed_after_snapshot: 'Suppressed after snapshot',
    kill_switch: 'Kill switch',
    cancelled: 'Cancelled',
    no_account_key: 'No account key',
    role_removed: 'Role removed',
    not_admin: 'Not admin'
};
// BE applies this when an ab_split step omits `ratio` (mark/cron.py _resolve_ab_split).
export const DEFAULT_AB_SPLIT_RATIO = 50;
